import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import asFunctional from 'sharedreact/components/asFunctional';
import { FilterMode } from 'sharedreact/constants/generic/Constants';
import Filter from 'sharedreact/data/Filter';
import type User from 'sharedreact/data/User';
import SettingsStore from 'sharedreact/stores/SettingsStore';
import StackedDialogStore from 'sharedreact/stores/StackedDialogStore';
import TablePreferencesStore from 'sharedreact/stores/TablePreferencesStore';
import { assertNonNullish } from 'sharedreact/utils/TypeSupport';

import UsersActions from 'workspaces/common/actions/UsersActions';
import UserSelectionDialog from 'workspaces/common/components/users/UserSelectionDialog';
import UsersStore from 'workspaces/common/stores/UsersStore';

const TABLE_NAME: string = 'UserSelectionTable';

type Props = {
  show: boolean,
  title: string,
  selection: Immutable.Set<string>,
  filterString: string,

  onClose: () => void,
  onSelect: (user: User) => void
};

type State = ConstructorParameters<typeof UserSelectionDialog>[0];

function getStores () {
  return [
    TablePreferencesStore,
    StackedDialogStore,
    UsersStore
  ];
}

function handleSetSelection (selection: Immutable.Set<string>): void {
  UsersActions.setSelection(selection);
  UsersActions.ensureSelectionIsVisible(true);
}

function handleSetFilter (filterString: string): void {
  UsersActions.setFilter(
    FilterMode.TEMP,
    Filter.createFromString(filterString, UsersStore.getFilterKeywords()),
    filterString
  );
  UsersActions.activateFilter(FilterMode.TEMP);
}

function handleSelect (props: Props): void {
  const selection = UsersStore.getSelection();

  if (selection.size === 1) {
    const user = UsersStore.getItem(selection.first());
    if (user != null) {
      props.onSelect(user);
    }
  }
}

function handleInit (props: Props): void {
  UsersActions.setFilter(
    FilterMode.TEMP,
    Filter.createFromString(props.filterString, UsersStore.getFilterKeywords()),
    props.filterString,
    true);
  UsersActions.setSortOrder(TablePreferencesStore.getTableSortOrder(
    TABLE_NAME,
    UsersStore.getInitialSortOrder()
  ));
  UsersActions.setSelection(props.selection);
  UsersActions.allowMultipleSelections(false);
  UsersActions.ensureSelectionIsVisible(true);
  UsersActions.activateFilter(FilterMode.TEMP);
}

function calculateState (prevState?: State, props?: Props): State {
  assertNonNullish(props);

  const selectedColumns: Immutable.Set<string> | undefined = TablePreferencesStore.getSelectedColumns(TABLE_NAME);

  return {
    show: props.show,
    language: SettingsStore.getLanguage(),
    title: props.title,
    users: UsersStore.getItems(),
    initialSelection: props.selection,
    selection: UsersStore.getSelection(),
    ensureSelectionIsVisible: UsersStore.getEnsureSelectionIsVisible(),
    selectionIndexes: UsersStore.getSelectionIndexes(),
    filter: UsersStore.getFilterString(),
    filterKeywords: UsersStore.getFilterKeywords(),
    sortOrder: UsersStore.getSortOrder(),
    selectedColumns: selectedColumns,

    setVisibleRange: UsersActions.setVisibleRange,
    setSelection: handleSetSelection,
    setFilter: handleSetFilter,
    setSortOrder: UsersActions.setSortOrder,
    onItemSelect: UsersActions.modifySelection,
    onEnsuredSelectionIsVisible: () => UsersActions.ensureSelectionIsVisible(false),

    onClose: props.onClose,
    onSelect: () => handleSelect(props),
    hideValidationPopups: (!props.show),

    onInit: () => handleInit(props)
  };
}

export default FluxUtils.Container.createFunctional(
  asFunctional(UserSelectionDialog),
  getStores,
  calculateState,
  { withProps: true }
);
